import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type ContentGapRow = {
  lessonId: string;
  lessonTitle: string;
  moduleTitle: string;
  completionPct: number;
  completedCount: number;
  enrolledCount: number;
};

type ParticipationRow = {
  userId: string;
  studentName: string | null;
  studentEmail: string;
  cohortId: string;
  enrolledAt: string;
  lastActiveAt: string | null;
  progressPercent: number;
  videoWatchPercent: number | null;
  quizAvgPct: number | null;
  homeworkSubmitted: number;
  homeworkTotal: number;
  homeworkGraded: number;
  homeworkOnTime: number;
  homeworkLate: number;
  homeworkMissing: number;
  homeworkPending: number;
  atRisk: boolean;
};

type ParticipationPayload = {
  totalStudents: number;
  atRiskCount: number;
  contentGaps: ContentGapRow[];
  rows: ParticipationRow[];
};

type Cohort = { id: string; name: string };

type CourseAnalyticsProps = {
  tenantSlug: string;
  courseId: string;
  courseTitle: string;
};

function formatScore(score: number | null | undefined): string {
  if (score == null) return "—";
  return `${score}%`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

export function CourseAnalytics({ tenantSlug, courseId, courseTitle }: CourseAnalyticsProps) {
  const t = useTranslations("instructor");
  // C4 (CRS-16): server errors are shown by code, never as the server's English.
  const tRoot = useTranslations();
  const errorText = (body: unknown, fallbackKey: string) =>
    apiErrorText(body, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [analytics, setAnalytics] = useState<ParticipationPayload | null>(null);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Aborts in flight when the instructor leaves, so res.json() cannot reject
    // on a truncated body with nothing to catch it.
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch(`/v1/instructor/courses/${courseId}/cohorts`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (res.ok) {
          setCohorts(json.data.cohorts as Cohort[]);
        }
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
      }
    })();
    return () => controller.abort();
  }, [courseId]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    void (async () => {
      setLoading(true);
      const query = cohortId ? `?cohortId=${encodeURIComponent(cohortId)}` : "";
      try {
        const res = await apiFetch(`/v1/instructor/courses/${courseId}/analytics${query}`, {
          signal: controller.signal,
        });
        const json = await res.json();
        if (!active) return;

        if (!res.ok) {
          setError(errorText(json, "analyticsLoadFailed"));
          setLoading(false);
          return;
        }

        setAnalytics(json.data as ParticipationPayload);
        setError(null);
        setLoading(false);
      } catch (err) {
        if (!active || (err as Error)?.name === "AbortError") return;
        setError(t("analyticsLoadFailed"));
        setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId, cohortId, t]);

  async function handleExportCsv() {
    setExporting(true);
    try {
      const query = new URLSearchParams({ format: "csv" });
      if (cohortId) query.set("cohortId", cohortId);
      const res = await apiFetch(`/v1/instructor/courses/${courseId}/analytics?${query}`, {
        method: "POST",
      });
      if (!res.ok) {
        setError(t("analyticsExportFailed"));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `participation-${courseId}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)]">{t("analyticsLoading")}</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }

  if (!analytics) {
    return <p className="text-sm text-[var(--text-muted)]">{t("analyticsUnavailable")}</p>;
  }

  return (
    <section data-testid="course-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("analyticsTitle")}</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{courseTitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm">
            <span>{t("analyticsCohortFilter")}</span>
            <select
              className="rounded border border-[var(--border-strong)] px-2 py-1.5"
              value={cohortId}
              onChange={(event) => setCohortId(event.target.value)}
              data-testid="analytics-cohort-filter"
            >
              <option value="">{t("analyticsCohortAll")}</option>
              {cohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={exporting}
            className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm disabled:opacity-50"
            data-testid="analytics-export-csv"
          >
            {exporting ? t("analyticsExporting") : t("analyticsExportCsv")}
          </button>
          <Link
            to={`/t/${tenantSlug}/instructor/courses/${courseId}`}
            className="rounded border border-[var(--border-strong)] px-3 py-1.5 text-sm"
          >
            {t("analyticsBackToBuilder")}
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-4 text-sm">
        <p data-testid="analytics-total-students">
          {t("analyticsTotalStudents", { count: analytics.totalStudents })}
        </p>
        <p data-testid="analytics-at-risk-count">
          {t("analyticsAtRiskCount", { count: analytics.atRiskCount })}
        </p>
      </div>

      {analytics.rows.length === 0 ? (
        <p className="mt-6 text-sm text-[var(--text-muted)]">{t("analyticsNoStudents")}</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="px-3 py-2 font-medium">{t("analyticsStudent")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsEmail")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsProgress")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsVideoWatch")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsQuizAvg")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsHomework")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsHomeworkTiming")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsLastActive")}</th>
                <th className="px-3 py-2 font-medium">{t("analyticsAtRisk")}</th>
              </tr>
            </thead>
            <tbody>
              {analytics.rows.map((row) => (
                <tr
                  key={row.userId}
                  className="border-b border-[var(--border)]"
                  data-testid={`analytics-row-${row.userId}`}
                >
                  <td className="px-3 py-2">{row.studentName ?? "—"}</td>
                  <td className="px-3 py-2">{row.studentEmail}</td>
                  <td className="px-3 py-2" data-testid={`analytics-progress-${row.userId}`}>
                    {formatScore(row.progressPercent)}
                  </td>
                  <td className="px-3 py-2" data-testid={`analytics-video-${row.userId}`}>
                    {formatScore(row.videoWatchPercent)}
                  </td>
                  <td className="px-3 py-2" data-testid={`analytics-quiz-${row.userId}`}>
                    {formatScore(row.quizAvgPct)}
                  </td>
                  <td className="px-3 py-2">
                    {row.homeworkSubmitted}/{row.homeworkTotal}
                    {row.homeworkGraded > 0
                      ? ` (${row.homeworkGraded} ${t("analyticsGraded")})`
                      : ""}
                  </td>
                  <td className="px-3 py-2" data-testid={`analytics-hw-timing-${row.userId}`}>
                    {t("analyticsHomeworkTimingValues", {
                      onTime: row.homeworkOnTime,
                      late: row.homeworkLate,
                      missing: row.homeworkMissing,
                      pending: row.homeworkPending,
                    })}
                  </td>
                  <td className="px-3 py-2">{formatDate(row.lastActiveAt)}</td>
                  <td className="px-3 py-2" data-testid={`analytics-at-risk-${row.userId}`}>
                    {row.atRisk ? t("analyticsAtRiskYes") : t("analyticsAtRiskNo")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8" data-testid="analytics-content-gaps">
        <h2 className="text-lg font-medium">{t("analyticsContentGapsTitle")}</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">{t("analyticsContentGapsHint")}</p>
        {analytics.contentGaps.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--text-muted)]">{t("analyticsContentGapsEmpty")}</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] text-left">
                  <th className="px-3 py-2 font-medium">{t("analyticsContentGapsModule")}</th>
                  <th className="px-3 py-2 font-medium">{t("analyticsContentGapsLesson")}</th>
                  <th className="px-3 py-2 font-medium">{t("analyticsContentGapsCompletion")}</th>
                  <th className="px-3 py-2 font-medium">{t("analyticsContentGapsStudents")}</th>
                </tr>
              </thead>
              <tbody>
                {analytics.contentGaps.map((gap) => (
                  <tr
                    key={gap.lessonId}
                    className="border-b border-[var(--border)]"
                    data-testid={`analytics-gap-${gap.lessonId}`}
                  >
                    <td className="px-3 py-2">{gap.moduleTitle}</td>
                    <td className="px-3 py-2">{gap.lessonTitle}</td>
                    <td className="px-3 py-2">{formatScore(gap.completionPct)}</td>
                    <td className="px-3 py-2">
                      {t("analyticsContentGapsCompleted", {
                        completed: gap.completedCount,
                        total: gap.enrolledCount,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
